import { useEffect, useState } from 'react';
import { api } from '../services/api';

export default function Settings() {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploaded, setUploaded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    api.getConfig().then(setConfig).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateConfig(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  const updateKey = (key: string, value: string) => {
    setConfig({ ...config, [key]: value });
  };

  const handleFileUpload = async (intent: string, language: string, topic: string | null, displayName: string, file: File | null) => {
    if (!file) return;
    const uploadKey = `${intent}_${language}_${topic || ''}`;
    setUploading({ ...uploading, [uploadKey]: true });
    try {
      await api.uploadVoiceFile(file, intent, language, topic, displayName);
      setUploaded({ ...uploaded, [uploadKey]: true });
      setTimeout(() => setUploaded(prev => ({ ...prev, [uploadKey]: false })), 2000);
    } catch (err) {
      alert('Upload failed: ' + (err as Error).message);
    }
    setUploading({ ...uploading, [uploadKey]: false });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 8, color: '#1e293b', fontSize: 14, outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block', fontWeight: 500,
  };

  const sections: Array<
    | { title: string; fields: Array<{key: string; label: string; type: string; options?: string[]}> }
    | { title: string; voiceFields: Array<{intent: string; label: string}>; languages: string[]; languageLabels: Record<string, string> }
  > = [
    {
      title: 'WhatsApp Configuration',
      fields: [
        { key: 'whatsapp_api_token', label: 'API Token', type: 'password' },
        { key: 'whatsapp_phone_number_id', label: 'Phone Number ID', type: 'text' },
        { key: 'whatsapp_verify_token', label: 'Verify Token', type: 'text' },
      ],
    },
    {
      title: 'Check — Verification Settings',
      fields: [
        { key: 'bot_name', label: 'Bot Name', type: 'text' },
        { key: 'default_language', label: 'Default Language', type: 'select', options: ['en', 'hausa', 'yoruba', 'igbo', 'french'] },
        { key: 'response_mode', label: 'Voice Response', type: 'select', options: ['text', 'voice', 'both'] },
        { key: 'fallback_message', label: 'Fallback Message', type: 'textarea' },
      ],
    },
    {
      title: 'Map — Early Warning Settings',
      fields: [
        { key: 'alert_threshold', label: 'Alert Threshold (similar messages)', type: 'number' },
        { key: 'alert_email', label: 'Alert Email', type: 'email' },
        { key: 'tracking_enabled', label: 'Misinfo Tracking', type: 'select', options: ['true', 'false'] },
        { key: 'refresh_interval', label: 'Dashboard Refresh (seconds)', type: 'number' },
      ],
    },
    {
      title: 'Inoculate — Prebunking Settings',
      fields: [
        { key: 'prebunk_enabled', label: 'Prebunking Enabled', type: 'select', options: ['true', 'false'] },
        { key: 'clip_languages', label: 'Clip Languages', type: 'text' },
        { key: 'messenger_groups', label: 'Messenger Groups', type: 'text' },
        { key: 'clip_duration', label: 'Clip Duration (seconds)', type: 'number' },
      ],
    },
    {
      title: 'Disease Fact Packs',
      fields: [
        { key: 'enabled_diseases', label: 'Enabled Diseases', type: 'text' },
        { key: 'lassa_enabled', label: 'Lassa Fever', type: 'select', options: ['true', 'false'] },
        { key: 'ebola_enabled', label: 'Ebola', type: 'select', options: ['true', 'false'] },
        { key: 'cholera_enabled', label: 'Cholera', type: 'select', options: ['true', 'false'] },
      ],
    },
    {
      title: 'Pre-recorded Voice Responses',
      voiceFields: [
        { intent: 'greeting', label: 'Greeting' },
        { intent: 'emergency', label: 'Emergency' },
        { intent: 'health_claim', label: 'Claim Verification' },
        { intent: 'health_question', label: 'Health Question' },
      ],
      languages: ['en', 'hausa', 'yoruba', 'igbo', 'french'],
      languageLabels: { en: 'English', hausa: 'Hausa', yoruba: 'Yoruba', igbo: 'Igbo', french: 'French' },
    },
  ];

  if (loading) return <div style={{ padding: 40 }}><span style={{ color: '#94a3b8' }}>Loading...</span></div>;

  return (
    <div style={{ padding: '32px 40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', letterSpacing: -0.5 }}>Settings</h1>
          <p style={{ fontSize: 14, color: '#94a3b8', marginTop: 4 }}>Configure OLU bot, Map, and Inoculation</p>
        </div>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          background: saved ? '#22c55e' : 'linear-gradient(135deg, #0891b2, #06b6d4)',
          color: '#ffffff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          boxShadow: saved ? '0 2px 8px #22c55e33' : '0 2px 8px #0891b233',
          transition: 'all 0.2s ease',
        }}>
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {sections.map(section => {
          if ('voiceFields' in section) {
            return (
              <div key={section.title} style={{
                background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 20 }}>{section.title}</h3>
                {section.voiceFields.map(vf => (
                  <div key={vf.intent} style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 14, fontWeight: 500, color: '#334155', marginBottom: 8 }}>{vf.label}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                      {section.languages.map(lang => {
                        const uploadKey = `${vf.intent}_${lang}_`;
                        return (
                          <div key={`${vf.intent}_${lang}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>{section.languageLabels[lang]}</span>
                            <input
                              type="file"
                              accept="audio/mpeg"
                              onChange={e => handleFileUpload(vf.intent, lang, null, `${vf.label} - ${section.languageLabels[lang]}`, e.target.files?.[0] || null)}
                              style={{ ...inputStyle, padding: 4, fontSize: 12 }}
                            />
                            {uploading[uploadKey] && <span style={{ fontSize: 11, color: '#0891b2' }}>Uploading...</span>}
                            {uploaded[uploadKey] && <span style={{ fontSize: 11, color: '#22c55e' }}>Uploaded!</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return (
          <div key={section.title} style={{
            background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 20 }}>{section.title}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {section.fields.map(field => (
                <div key={field.key} style={{ gridColumn: field.type === 'textarea' ? '1 / -1' : undefined }}>
                  <label style={labelStyle}>{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      value={config[field.key] || ''}
                      onChange={e => updateKey(field.key, e.target.value)}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">Select...</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={config[field.key] || ''}
                      onChange={e => updateKey(field.key, e.target.value)}
                      style={{ ...inputStyle, minHeight: 80, resize: 'vertical' as const }}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={config[field.key] || ''}
                      onChange={e => updateKey(field.key, e.target.value)}
                      style={inputStyle}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )})}
      </div>
    </div>
  );
}
